// Wunschliste (Gast-Variante): Produkte liegen als Liste von Handles im localStorage des Geräts. Herz-Buttons
// (<fk-wishlist-button>) schalten um, der Header zeigt die Anzahl, der Drawer (<fk-wishlist-drawer>) lädt die
// Produkte über die Ajax-API (/products/<handle>.js) und baut daraus Karten. Später kann ein Backend die Liste
// im Kundenkonto spiegeln – die Schnittstelle (Store-Objekt) bleibt dann gleich.

const KEY = 'fk-wishlist';
const EVENT = 'fk:wishlist:change';

const store = {
  read() {
    try {
      const v = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(v) ? v.filter((x) => x && x.handle) : [];
    } catch {
      return [];
    }
  },
  write(items) {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch {
      /* Speicher voll oder gesperrt */
    }
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { items } }));
  },
  has(handle) {
    return this.read().some((x) => x.handle === handle);
  },
  toggle(handle, id) {
    const items = this.read();
    const i = items.findIndex((x) => x.handle === handle);
    if (i >= 0) items.splice(i, 1);
    else items.unshift({ handle, id, t: Date.now() });
    this.write(items);
    return i < 0;
  },
  remove(handle) {
    this.write(this.read().filter((x) => x.handle !== handle));
  },
};
window.fkWishlist = store;

// Analytics events for assets/fk-analytics.js; a no-op while analytics is off.
const track = (event, properties) => document.dispatchEvent(new CustomEvent('fk:track', { detail: { event, properties } }));

/* ---------- Herz-Button ---------- */
class FkWishlistButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.onClick);
    this.sync();
    document.addEventListener(EVENT, this.sync);
  }
  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
    document.removeEventListener(EVENT, this.sync);
  }
  onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const added = store.toggle(this.dataset.handle, this.dataset.id);
    track(added ? 'wishlist_item_added' : 'wishlist_item_removed', {
      product_handle: this.dataset.handle, variant_id: Number(this.dataset.id) || undefined,
      placement: this.classList.contains('fk-wish--square') ? 'product_page' : 'card', items_count: store.read().length,
    });
    const status = this.querySelector('[data-status]');
    if (status) status.textContent = added ? this.dataset.addedText || 'Added to wishlist' : this.dataset.removedText || 'Removed from wishlist';
    if (added) {
      this.classList.add('fk-wish--pop');
      setTimeout(() => this.classList.remove('fk-wish--pop'), 450);
    }
  };
  sync = () => {
    const on = store.has(this.dataset.handle);
    this.toggleAttribute('data-active', on);
    this.button?.setAttribute('aria-pressed', on ? 'true' : 'false');
    this.button?.setAttribute('aria-label', on ? this.dataset.labelRemove || 'Remove from wishlist' : this.dataset.labelAdd || 'Add to wishlist');
  };
}

/* ---------- Zähler im Header ---------- */
function syncCount() {
  const n = store.read().length;
  document.querySelectorAll('[data-fk-wishlist-count]').forEach((el) => {
    el.textContent = n > 99 ? '99+' : String(n);
    el.hidden = n === 0;
    el.classList.toggle('visually-hidden', n === 0);
    el.closest('[data-fk-wishlist-trigger]')?.toggleAttribute('data-has-items', n > 0);
  });
}
document.addEventListener(EVENT, syncCount);

/* ---------- Drawer ---------- */
// Shopify-Geldformate ({{amount}}, {{amount_with_comma_separator}} …) für Beträge in Cent
const money = (cents, format) => {
  const value = Math.round(Number(cents) || 0);
  const fmt = format || '{{amount}}';
  const pattern = /\{\{\s*(\w+)\s*\}\}/;
  const type = (fmt.match(pattern) || [])[1] || 'amount';
  const group = (decimals, thousands, decimal) => {
    const [int, frac] = (Math.abs(value) / 100).toFixed(decimals).split('.');
    return `${value < 0 ? '-' : ''}${int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands)}${frac ? decimal + frac : ''}`;
  };
  const amounts = {
    amount: () => group(2, ',', '.'),
    amount_no_decimals: () => group(0, ',', '.'),
    amount_with_comma_separator: () => group(2, '.', ','),
    amount_no_decimals_with_comma_separator: () => group(0, '.', ','),
    amount_with_space_separator: () => group(2, ' ', ','),
    amount_no_decimals_with_space_separator: () => group(0, ' ', ''),
    amount_with_apostrophe_separator: () => group(2, "'", '.'),
    amount_with_period_and_space_separator: () => group(2, ' ', '.'),
  };
  return fmt.replace(pattern, (amounts[type] || amounts.amount)());
};

class FkWishlistDrawer extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-list]');
    this.empty = this.querySelector('[data-empty]');
    this.format = this.dataset.moneyFormat;
    this.rendered = '';
    const drawer = this.closest('theme-drawer');
    this.drawer = drawer;
    this.onOpen = () => this.render();
    this.onChange = () => { if (drawer?.hasAttribute('open')) this.render(); };
    drawer?.addEventListener('theme-drawer:open', this.onOpen);
    document.addEventListener(EVENT, this.onChange);
    // Wird beim Laden gerendert, falls der Drawer schon offen ist (Zurück-Navigation)
    if (drawer?.hasAttribute('open')) this.render();
  }

  disconnectedCallback() {
    this.generation = (this.generation || 0) + 1;
    this.drawer?.removeEventListener('theme-drawer:open', this.onOpen);
    document.removeEventListener(EVENT, this.onChange);
  }

  async render() {
    const items = store.read();
    const generation = this.generation = (this.generation || 0) + 1;
    this.empty.hidden = items.length > 0;
    this.list.innerHTML = '';
    const total = this.querySelector('[data-wishlist-total]');
    total.hidden = true;
    if (!items.length) { this.list.removeAttribute('aria-busy'); return; }
    this.list.setAttribute('aria-busy', 'true');
    const products = await Promise.all(items.map(async item => {
      if (!item.bundle) return this.load(item.handle, item.id);
      const picks = await Promise.all((item.bundle.picks || []).map(async pick => {
        const product = await this.load(pick.handle, pick.variant);
        return product ? { ...pick, price: product.price, title: product.title, image: product.image, available: product.available, verified: true } : { ...pick, price: null, available: false, verified: false };
      }));
      return { ...item, picks, price: picks.reduce((sum, pick) => sum + (pick.verified ? pick.price * (pick.quantity || 1) : 0), 0), complete: picks.every(pick => pick.verified) };
    }));
    if (generation !== this.generation) return;
    let sum = 0, complete = true;
    products.forEach((product, index) => {
      if (product) {
        sum += product.price;
        complete &&= product.complete !== false;
        this.list.appendChild(product.bundle ? this.bundleCard(product) : this.card(product));
      } else {
        complete = false;
        const li = document.createElement('li');
        li.className = 'fk-wishd__item';
        li.textContent = this.dataset.unavailableLabel;
        const remove = document.createElement('button');
        remove.type = 'button'; remove.className = 'fk-wishd__remove'; remove.textContent = this.dataset.removeLabel;
        remove.addEventListener('click', () => store.remove(items[index].handle));
        li.append(remove); this.list.append(li);
      }
    });
    total.hidden = false;
    this.querySelector('[data-wishlist-amount]').innerHTML = complete ? money(sum, this.format) : this.dataset.unavailableLabel;
    this.list.removeAttribute('aria-busy');
  }

  async load(handle, variantId) {
    if (!/^[a-z0-9][a-z0-9-]*$/i.test(handle || '')) return null;
    const root = window.Shopify?.routes?.root || '/';
    for (let attempt = 0; attempt < 2; attempt++) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const res = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' }, signal: controller.signal });
        if (!res.ok) {
          if (res.status === 408 || res.status === 429 || res.status >= 500) throw new Error('temporary_product_error');
          return null;
        }
        const p = await res.json();
        const variant = variantId ? p.variants?.find(v => String(v.id) === String(variantId)) : p.variants?.find(v => v.available) || p.variants?.[0];
        if (!variant) return null;
        return {
          handle: p.handle, title: p.title, vendor: p.vendor, url: `${root}products/${p.handle}`, available: variant?.available,
          image: variant?.featured_image?.src || p.featured_image, price: variant?.price ?? p.price, compare: variant?.compare_at_price,
          variantId: variant?.id, single: p.variants?.length === 1,
        };
      } catch {
        if (attempt === 1) return null;
      } finally { clearTimeout(timeout); }
      await new Promise(resolve => setTimeout(resolve, 300));
    }
  }

  bundleCard(item) {
    const li = document.createElement('li'); li.className = 'fk-wishd__item fk-wishd__item--bundle';
    const picks = item.picks.filter(Boolean);
    li.innerHTML = `<div class="fk-wishd__body"><p class="fk-wishd__vendor">${escapeHtml(this.dataset.bundleLabel)}</p><h3 class="fk-wishd__title">${escapeHtml(item.title)}</h3><ul class="fk-wishd__bundle-items">${picks.map(p => `<li>${escapeHtml(p.title)} × ${Number(p.quantity) || 1}${p.verified === false ? ` — ${escapeHtml(this.dataset.unavailableLabel)}` : ''}</li>`).join('')}</ul><p class="fk-wishd__price">${item.complete ? money(item.price, this.format) : escapeHtml(this.dataset.unavailableLabel)}</p><div class="fk-wishd__actions"><button type="button" class="fk-button" data-restore ${item.complete ? '' : 'disabled'}>${escapeHtml(this.dataset.editBundleLabel)}</button><button type="button" class="fk-wishd__remove" data-remove>${escapeHtml(this.dataset.removeLabel)}</button></div></div>`;
    li.querySelector('[data-remove]').addEventListener('click', () => store.remove(item.handle));
    li.querySelector('[data-restore]').addEventListener('click', async () => {
      try {
        sessionStorage.setItem('fk-bundle-steps:v4', JSON.stringify({ ...item.bundle, picks, view: 'review', bundleId: null, cartLinked: false, cartToken: null, draft: true }));
        // Horizon restores open drawers across navigation; close before leaving.
        await this.drawer?.close();
        location.assign(`${window.Shopify?.routes?.root || '/'}?view=bundle-builder`);
      } catch { li.querySelector('[data-restore]').textContent = this.dataset.storageError; }
    });
    return li;
  }

  card(p) {
    const li = document.createElement('li');
    li.className = 'fk-wishd__item';
    const img = p.image ? `${p.image}${p.image.includes('?') ? '&' : '?'}width=240` : '';
    const sale = p.single && p.compare && p.compare > p.price;
    li.innerHTML = `
      <a class="fk-wishd__media" href="${p.url}" tabindex="-1" aria-hidden="true">${img ? `<img src="${img}" alt="" loading="lazy" width="120" height="120">` : ''}</a>
      <div class="fk-wishd__body">
        ${p.vendor ? `<p class="fk-wishd__vendor">${escapeHtml(p.vendor)}</p>` : ''}
        <h3 class="fk-wishd__title"><a href="${p.url}">${escapeHtml(p.title)}</a></h3>
        <p class="fk-wishd__price"><span${sale ? ' class="fk-wishd__price--sale"' : ''}>${money(p.price, this.format)}</span>${sale ? ` <span class="fk-price-saving">${(this.dataset.saveLabel || 'Save [amount]').replace('[amount]', money(p.compare - p.price, this.format))}</span>` : ''}</p>
        <div class="fk-wishd__actions">
          ${p.available && p.single && p.variantId
            ? `<fk-add-button data-product-id="${p.handle}"><form method="post" action="${window.Shopify?.routes?.root || '/'}cart/add" class="fk-wishd__form"><input type="hidden" name="id" value="${p.variantId}"><input type="hidden" name="quantity" value="1"><button type="submit" class="fk-button fk-wishd__add">${escapeHtml(this.dataset.addLabel || 'Add to cart')}</button></form></fk-add-button>`
            : `<a class="fk-button fk-button--outline fk-wishd__add" href="${p.url}">${escapeHtml(p.available ? this.dataset.chooseLabel || 'Choose options' : this.dataset.soldOutLabel || 'Sold out')}</a>`}
          <button type="button" class="fk-wishd__remove" data-remove="${p.handle}" aria-label="${escapeHtml(`${this.dataset.removeLabel || 'Remove'}: ${p.title}`)}">${escapeHtml(this.dataset.removeLabel || 'Remove')}</button>
        </div>
      </div>`;
    li.querySelector('[data-remove]').addEventListener('click', () => {
      store.remove(p.handle);
      track('wishlist_item_removed', { product_handle: p.handle, placement: 'drawer', items_count: store.read().length });
      li.remove();

    });
    return li;
  }
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

if (!customElements.get('fk-wishlist-button')) customElements.define('fk-wishlist-button', FkWishlistButton);
if (!customElements.get('fk-wishlist-drawer')) customElements.define('fk-wishlist-drawer', FkWishlistDrawer);
syncCount();
